import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { DistributorRegistrationForm, EmailVerificationForm } from "@/components";

import { useSignUpUser, useEmailVerification, useResendEmailVerificationCode } from "@/hooks";

import { distributorRegistrationSchema } from "@/schemas/DistributorRegistrationSchema";
import { BusinessFormValues } from "@/schemas/sellerRegistrationSchema";

import { createSellerInfo } from "@/graphql/mutations";

import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import ls from "@/utils/SecureLS";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch } from "@/store/hooks";
import { setDistributorInfo, setUser } from "@/store/slices/authSlice";

import { CheckCircle } from "lucide-react";
import DistributorSignUpForm from "@/components/distributorSignUpForm/DistributorSignUpForm";

import { useCreateUserInfo } from "@/hooks/useCreateUserInfo";
import {  UserInfo } from "@/types/AppTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type WizardStep = "signup" | "verification" | "business-info" | "complete";

interface WizardState {
  step: WizardStep;
  distributorData?: z.infer<typeof distributorRegistrationSchema>;
  cognitoUserId?: string;
  email?: string;
  isEmailVerified?: boolean;
}

const WIZARD_STORAGE_KEY = "distributor-wizard-state";

const DistributorRegistration = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { create, error, data } = useCreateUserInfo();

  const [wizardState, setWizardState] = useState<WizardState>(() => {
    const saved = localStorage.getItem(WIZARD_STORAGE_KEY);
    const urlStep = searchParams.get("step") as WizardStep;

    if (saved) {
      const parsedState = JSON.parse(saved);
      if (urlStep && ["signup", "verification", "business-info"].includes(urlStep)) {
        return { ...parsedState, step: urlStep };
      }
      return parsedState;
    }

    return {
      step: urlStep || "signup",
    };
  });

  const [loading, setLoading] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);

  const { handleSignUp, loading: signupLoading } = useSignUpUser();
  const otpRef = React.useRef<{ resetOtp: () => void }>(null);

  const { completeSignUp } = useEmailVerification(otpRef, setEmailVerificationLoading,  {
    autoNavigate: false,
    email: wizardState.email || "",
    role: "distributor",
    onSuccess: (user : UserInfo) => {
      setWizardState((prev) => ({
        ...prev,
        step: "business-info",
        isEmailVerified: true,
      }));

      toast.success("Email verified successfully!", {
        description: "Now let's set up your business information.",
      });
    },
    onError: (error: any) => {
      console.error("Email verification error:", error);
    },

  },

);

  const { resendVerificationCode } = useResendEmailVerificationCode();

  useEffect(() => {
    if (wizardState.step !== "complete") {
      setSearchParams({ step: wizardState.step });
    }
  }, [wizardState.step, setSearchParams]);

  useEffect(() => {
    if (wizardState.step !== "complete") {
      localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(wizardState));
    } else {
      localStorage.removeItem(WIZARD_STORAGE_KEY);
    }
  }, [wizardState]);

  const handleDistributorSignup = async (
    data: z.infer<typeof distributorRegistrationSchema>
  ): Promise<void> => {
    try {
      const result = await handleSignUp({
        email: data?.email,
        password: data.password,
        firstName: data?.firstName,
        lastName: data?.lastName,
        autoNavigate: false,
      });

      if (result?.userId) {
        setWizardState((prev) => ({
          ...prev,
          step: "verification",
          distributorData: data,
          cognitoUserId: result.userId,
          email: data.email,
          role: "seller",
        }));

        toast.success("Please check your email for verification code");
      }
    } catch (error) {
      console.error("Distributor signup error:", error);
    }
  };

  const handleEmailVerification = async (verificationCode: string) => {
    try {
      await completeSignUp(verificationCode);
    } catch (error) {
      console.error("Email verification error:", error);
    }
  };

  const handleBusinessInfoSubmit = async (data: BusinessFormValues) => {
    try {
      setLoading(true);

      const userInfo = await create({
        id: uuidv4(),
        user_id: wizardState.cognitoUserId || "",
        email: wizardState.email || "",
        first_name: wizardState?.distributorData?.firstName || "",
        last_name: wizardState?.distributorData?.lastName || "",
        has_roles: ["seller"],
        current_role: "seller",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });


      if(!userInfo) {
        toast.error("Failed to create user info");
        return;
    }

      const sellerBusinessInfo = await executeGraphQLMutation({
        query: createSellerInfo,
        variables: {
          input: {
            id: uuidv4(),
            user_id: userInfo?.user_id,
            business_name: data?.businessName,
            description: data?.description,
            phone: data?.phone,
            street: data?.street,
            suite: data?.suite || "",
            city: data?.city,
            state: data?.state,
            country: data?.country,
            postal_code: data?.zipCode,
            is_verified: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        },
      });


      if (sellerBusinessInfo?.id) {
        //ls.set("user", JSON.stringify(userInfo));
       // dispatch(setDistributorInfo(sellerBusinessInfo));
//dispatch(setUser(userInfo));

        setWizardState((prev) => ({ ...prev, step: "complete" }));


    
      }
    } catch (error) {
      console.error("Business info submission error:", error);
      toast.error("Failed to complete registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const goToStep = (step: WizardStep) => {
    setWizardState((prev) => ({ ...prev, step }));
  };

  const ProgressIndicator = () => {
    const steps = [
      { key: "signup", label: "Create Account", description: "Basic information" },
      { key: "verification", label: "Verify Email", description: "Confirm your email" },
      { key: "business-info", label: "Business Setup", description: "Company details" },
    ];

    const currentStepIndex = steps.findIndex((s) => s.key === wizardState.step);

    return (
      <div className="mb-8">
        <div className="flex items-center justify-center max-w-2xl mx-auto relative">
          {steps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            const isNext = index > currentStepIndex;

            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center z-10 bg-gray-50 px-4">
                  <div
                    className={`
                      flex items-center justify-center w-10 h-10 rounded-full border-2 mb-3
                      ${isCompleted ? "bg-green-500 border-green-500 text-white" : ""}
                      ${isCurrent ? "border-blue-500 bg-blue-500 text-white" : ""}
                      ${isNext ? "border-gray-300 bg-white text-gray-400" : ""}
                    `}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <span className="text-sm font-semibold">{index + 1}</span>
                    )}
                  </div>
                  <div className="text-center max-w-32">
                    <div
                      className={`text-sm font-semibold mb-1 ${
                        isCurrent
                          ? "text-blue-500"
                          : isCompleted
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      {step.label}
                    </div>
                    <div className="text-xs text-gray-400 leading-tight">{step.description}</div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="flex-1 h-px relative -mx-4">
                    <div
                      className={`absolute top-0 left-0 right-0 h-px ${
                        index < currentStepIndex ? "bg-green-500" : "bg-gray-300"
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  // Render appropriate step content
  const renderStepContent = () => {
    switch (wizardState.step) {
      case "signup":
        return (
          <div className="max-w-2xl mx-auto">
            <DistributorSignUpForm onSubmit={handleDistributorSignup} loading={signupLoading} />
          </div>
        );

      case "verification":
        return (
          <div className="max-w-xl mx-auto">
            <EmailVerificationForm
              ref={otpRef}
              completeSignUp={handleEmailVerification}
              loading={emailVerificationLoading}
              setLoading={setEmailVerificationLoading}
              resendVerificationCode={resendVerificationCode}
              email={wizardState.email || ""}
              className="py-8"
            />
          </div>
        );

      case "business-info":
        return (
          <div className="max-w-4xl mx-auto">
            <DistributorRegistrationForm onSubmit={handleBusinessInfoSubmit} loading={loading} />
          </div>
        );

      case "complete":
        return (
          <div className="max-w-xl mx-auto text-center py-16">
            <Card>
              <CardContent className="pt-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Registration Complete!</h2>
                <p className="text-gray-600 mb-6">
                  Your distributor account has been created successfully. But you need to wait for admin approval.
                </p>
                <Button onClick={() => navigate("/")}>Go to Home</Button>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-full bg-gray-50 py-24">
      <div className="container mx-auto px-4 max-w-6xl ">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Become a Distributor</h1>
        </div>

        {/* Progress Indicator */}
        {wizardState.step !== "complete" && <ProgressIndicator />}

        {/* Step Content */}
        {renderStepContent()}
      </div>
    </div>
  );
};

export default DistributorRegistration;
